import {useMutation} from "@tanstack/react-query"
import {Radio, Space, Typography} from "antd"
import moment from "moment"
import {BaseButton, BaseInput} from "components"
import {formatSavedPaymentMethodLabel, handleError, PAYMENT_METHOD_NEW, toastSuccess} from "helpers"
import {useEffect, useState} from "react"
import {useTranslation} from "react-i18next"
import {AuthNetEnvironment, DispatchDataResponse, useAcceptJs} from "react-acceptjs"
import {usePaymentProviderConfigs} from "hooks/usePaymentProviderConfigs"
import {useStudentPaymentMethods} from "hooks"
import {studentPaymentService} from "services"
import {
    CreateRecurringPaymentPayload,
    PaymentProvider,
    RecurringPaymentInterval,
    StudentPaymentSupportedCurrency
} from "types/student-account/student-payment"
import styles from "./RecurringPayments.module.css"
import {FormLabel} from "components/Form"

type Props = {
    profileId: number
    amountInCents: number
    currency: StudentPaymentSupportedCurrency
    interval: RecurringPaymentInterval
    intervalCount?: number
    cancelAt: moment.Moment | null
    paymentPlanItems?: CreateRecurringPaymentPayload["paymentPlanItems"]
    paymentPlanId?: number | null
    onSuccess: () => void
}

export const AuthorizeNetRecurringSubscriptionForm = ({
    profileId,
    amountInCents,
    currency,
    interval,
    intervalCount,
    cancelAt,
    paymentPlanItems,
    paymentPlanId,
    onSuccess
}: Props) => {
    const {t} = useTranslation(["payment", "common"])
    const {paymentProviderConfigs} = usePaymentProviderConfigs(profileId)
    const providerConfig = paymentProviderConfigs.find((c) => c.provider === PaymentProvider.AuthorizeNet) as any

    const authData = {
        apiLoginID: providerConfig?.loginId ?? "",
        clientKey: providerConfig?.clientKey ?? ""
    }

    const {dispatchData, loading: acceptJsLoading} = useAcceptJs({
        environment: ((providerConfig?.paymentEnvironment ?? "SANDBOX") as string).toUpperCase() as AuthNetEnvironment,
        authData
    })

    const [paymentMethodType, setPaymentMethodType] = useState<"card" | "ach">("card")

    const [cardNumber, setCardNumber] = useState("")
    const [expiry, setExpiry] = useState("")
    const [cardCode, setCardCode] = useState("")
    const [cardholderName, setCardholderName] = useState("")

    const [bankAccountType, setBankAccountType] = useState<"checking" | "savings">("checking")
    const [routingNumber, setRoutingNumber] = useState("")
    const [accountNumber, setAccountNumber] = useState("")
    const [nameOnAccount, setNameOnAccount] = useState("")

    const {paymentMethods: allPaymentMethods} = useStudentPaymentMethods({profileId})
    const paymentMethods = allPaymentMethods.filter((method) => method.provider === PaymentProvider.AuthorizeNet)
    const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<number | undefined>()

    // Auto-select the active saved payment method (or the first one) once methods load; falls back
    // to "add new" if the student has no saved Authorize.net payment method yet.
    useEffect(() => {
        setSelectedPaymentMethodId((prev) => {
            if (prev !== undefined) return prev
            if (!paymentMethods.length) return PAYMENT_METHOD_NEW
            const active = paymentMethods.find((m) => m.isActive) ?? paymentMethods[0]
            return active.paymentMethodId
        })
    }, [paymentMethods])

    const selectedIsNewMethod = selectedPaymentMethodId === undefined || selectedPaymentMethodId === PAYMENT_METHOD_NEW

    const {isLoading, mutate: submit} = useMutation({
        mutationFn: async () => {
            if (!selectedIsNewMethod) {
                // A saved payment method is already on file with Authorize.net — no need to collect a
                // card form or call Accept.js at all.
                return studentPaymentService.createAuthorizeNetRecurringSubscription({
                    profileId,
                    amountInCents,
                    currency,
                    interval,
                    intervalCount,
                    cancelAt: cancelAt?.toISOString() ?? null,
                    paymentMethodId: selectedPaymentMethodId,
                    paymentPlanItems,
                    ...(paymentPlanId ? {paymentPlanId} : {})
                })
            }

            let response: DispatchDataResponse

            if (paymentMethodType === "ach") {
                if (!nameOnAccount.trim())
                    throw new Error(t("common:validation.cantEmpty", {field: "Account Holder Name"}))
                if (!routingNumber.trim()) throw new Error(t("common:validation.cantEmpty", {field: "Routing Number"}))
                if (!accountNumber.trim()) throw new Error(t("common:validation.cantEmpty", {field: "Account Number"}))

                response = await dispatchData({
                    bankData: {
                        accountType: bankAccountType,
                        routingNumber: routingNumber.trim(),
                        accountNumber: accountNumber.trim(),
                        nameOnAccount: nameOnAccount.trim(),
                        echeckType: "WEB"
                    } as any
                })
            } else {
                if (!cardholderName.trim())
                    throw new Error(t("common:validation.cantEmpty", {field: "Cardholder Name"}))
                if (!cardNumber.trim()) throw new Error(t("common:validation.cantEmpty", {field: "Card Number"}))
                if (!expiry.trim()) throw new Error(t("common:validation.cantEmpty", {field: "Expiry"}))
                if (!cardCode.trim()) throw new Error(t("common:validation.cantEmpty", {field: "Card Code"}))

                const expiryParts = expiry.split("/")
                response = await dispatchData({
                    cardData: {
                        fullName: cardholderName,
                        cardNumber: cardNumber.replace(/\s/g, ""),
                        month: expiryParts[0] ?? "",
                        year: expiryParts[1] ?? "",
                        cardCode,
                        zip: ""
                    }
                })
            }

            if (!response?.opaqueData) {
                throw new Error(t("msg.recurringPayment.failedToCreate"))
            }

            return studentPaymentService.createAuthorizeNetRecurringSubscription({
                profileId,
                amountInCents,
                currency,
                interval,
                intervalCount,
                cancelAt: cancelAt?.toISOString() ?? null,
                authorizeNetPaymentNonce: {opaqueData: response.opaqueData},
                paymentPlanItems,
                ...(paymentPlanId ? {paymentPlanId} : {})
            })
        },
        onError: (error: any) => {
            const acceptJsText = error?.messages?.message?.[0]?.text
            handleError(acceptJsText ? new Error(acceptJsText) : error)
        },
        onSuccess: () => {
            toastSuccess(t("msg.recurringPayment.addedSuccessfully"))
            onSuccess()
        }
    })

    if (amountInCents <= 0) {
        return (
            <Typography.Paragraph type="secondary">
                {t("msg.recurringPayment.enterAmount", "Please enter an amount greater than 0")}
            </Typography.Paragraph>
        )
    }

    return (
        <div>
            <FormLabel label={t("common:label.paymentMethods", "Payment Method")} />
            <Radio.Group
                className="mb-8"
                onChange={(e) => setSelectedPaymentMethodId(e.target.value)}
                value={selectedPaymentMethodId}>
                <Space direction="vertical">
                    {paymentMethods.map((method) => (
                        <Radio key={method.paymentMethodId} value={method.paymentMethodId}>
                            {formatSavedPaymentMethodLabel(method)}
                        </Radio>
                    ))}
                    <Radio value={PAYMENT_METHOD_NEW}>
                        {t("common:label.addNewPaymentMethod", "Add new payment method")}
                    </Radio>
                </Space>
            </Radio.Group>

            {selectedIsNewMethod && (
                <Radio.Group
                    className="mb-8 mt-8"
                    value={paymentMethodType}
                    onChange={(e) => setPaymentMethodType(e.target.value)}>
                    <Radio value="card">Card</Radio>
                    <Radio value="ach">Bank Account (ACH)</Radio>
                </Radio.Group>
            )}

            {selectedIsNewMethod && paymentMethodType === "ach" && (
                <>
                    <div className={styles.payFields}>
                        <FormLabel label="Account Holder Name" />
                        <BaseInput value={nameOnAccount} onChange={setNameOnAccount} placeholder="John Doe" />
                    </div>
                    <div className={styles.payFields}>
                        <FormLabel label="Account Type" />
                        <Radio.Group value={bankAccountType} onChange={(e) => setBankAccountType(e.target.value)}>
                            <Radio value="checking">Checking</Radio>
                            <Radio value="savings">Savings</Radio>
                        </Radio.Group>
                    </div>
                    <div className={styles.payFields}>
                        <FormLabel label="Routing Number" />
                        <BaseInput
                            value={routingNumber}
                            onChange={setRoutingNumber}
                            placeholder="9-digit routing number"
                            maxLength={9}
                        />
                    </div>
                    <div className={styles.payFields}>
                        <FormLabel label="Account Number" />
                        <BaseInput
                            value={accountNumber}
                            onChange={setAccountNumber}
                            placeholder="Bank account number"
                        />
                    </div>
                </>
            )}

            {selectedIsNewMethod && paymentMethodType === "card" && (
                <>
                    <div className={styles.payFields}>
                        <FormLabel label="Cardholder Name" />
                        <BaseInput value={cardholderName} onChange={setCardholderName} placeholder="John Doe" />
                    </div>
                    <div className={styles.payFields}>
                        <FormLabel label="Card Number" />
                        <BaseInput
                            value={cardNumber}
                            onChange={(value) => {
                                const cleaned = value.replace(/\s/g, "")
                                const formatted = cleaned.match(/.{1,4}/g)?.join(" ") ?? cleaned
                                setCardNumber(formatted)
                            }}
                            placeholder="xxxx xxxx xxxx xxxx"
                            maxLength={23}
                        />
                    </div>
                    <div className={styles.payFields}>
                        <FormLabel label="Expiry" />
                        <BaseInput
                            value={expiry}
                            onChange={(value) => {
                                const formatted =
                                    value.trim().length > 2 ? value.replace(/\//g, "").replace(/^(.{2})/, "$1/") : value
                                setExpiry(formatted)
                            }}
                            placeholder="mm/yyyy"
                            maxLength={7}
                        />
                    </div>
                    <div className={styles.payFields}>
                        <FormLabel label="Card Code" />
                        <BaseInput value={cardCode} onChange={setCardCode} placeholder="xxx" maxLength={4} />
                    </div>
                </>
            )}
            <BaseButton
                size="small"
                title={t("common:action.add")}
                className="w-full mt-24"
                loading={isLoading || acceptJsLoading}
                onClick={() => submit()}
            />
        </div>
    )
}
